import React, { useLayoutEffect, useRef, useState } from "react"
import styled, { keyframes } from "styled-components"
import { MobergFontFamily } from "../MobergFont/MobergFont"
import { MdMenuBook, MdOpenInNew } from "react-icons/md"
// Lucide's panel icons: the modern rounded rectangle with a divider, and they say which
// side the panel is on. This rail is on the right, so these are literally its picture.
import { LuPanelRightClose, LuPanelRightOpen, LuBookOpen, LuPenSquare, LuSearch } from "react-icons/lu"
import { ClaraGuide } from "./ClaraGuide"
import claraLogo from "../../assets/images/clara-logo.png"

// Clara's right rail: new chat, past chats, and the documents behind the current answer.
//
// It replaces two header buttons that opened a dropdown over the conversation. Sources
// were the reason: they belong somewhere persistent, not stacked under a message where
// they push the answer off screen and vanish on the next question. A rail keeps the
// evidence beside the answer for as long as the clinician wants it, which is the whole
// point of showing evidence at all.
//
// On the right, and collapsible to a strip of icons: at a bedside the conversation is
// the thing being read, and navigation should not hold a column of it hostage.

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`

const fadeUp = keyframes`
	from { opacity: 0; transform: translateY(3px); }
	to   { opacity: 1; transform: translateY(0); }
`

function timeAgo(iso) {
	if (!iso) return ""
	const seconds = (Date.now() - new Date(iso).getTime()) / 1000
	if (seconds < 60) return "now"
	if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
	if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
	return `${Math.floor(seconds / 86400)}d`
}

function locationOf(source) {
	const parts = []
	if (source.section) parts.push(source.section)
	if (source.page_start) parts.push(`page ${source.page_start}`)
	return parts.join(" · ")
}

/**
 * @param conversations past chats
 * @param activeId      the open conversation
 * @param sources       documents behind the most recent answer
 * @param onNewChat     start a fresh chat
 * @param onOpen        open a past chat
 */
export function ClaraSidebar({
	conversations = [],
	activeId = null,
	sources = [],
	onNewChat,
	onOpen,
	collapsed = false,
	onToggle,
}) {
	const [query, setQuery] = useState("")
	// Search is an icon until it is wanted, the way a rail that mostly lists things
	// should be. Opening it keeps whatever was already typed.
	const [searching, setSearching] = useState(false)
	const [guideOpen, setGuideOpen] = useState(false)
	const [hovered, setHovered] = useState(null)
	const [box, setBox] = useState(null)
	const [openSource, setOpenSource] = useState(null)
	const listRef = useRef(null)
	const itemRefs = useRef({})

	// One highlight that slides between rows, rather than a background per row. It reads
	// as a single object tracking the pointer instead of many things blinking.
	useLayoutEffect(() => {
		const container = listRef.current
		const target = itemRefs.current[hovered ?? activeId]
		if (!container || !target) {
			setBox(null)
			return
		}
		const containerRect = container.getBoundingClientRect()
		const targetRect = target.getBoundingClientRect()
		setBox({ top: targetRect.top - containerRect.top, height: targetRect.height })
	}, [hovered, activeId, conversations, query])

	const filtered = query.trim()
		? conversations.filter(c =>
				`${c.patient_label || ""} ${c.title || c.question || ""}`.toLowerCase().includes(query.toLowerCase())
		  )
		: conversations

	if (collapsed) {
		return (
			<Rail>
				{/* The mark IS the control. It sits alone at the top, and on hover it becomes
				    the panel icon, so the thing you are about to do replaces the thing you
				    were looking at. Below it, after a gap, the three actions. */}
				<Brand type="button" onClick={onToggle} aria-label="Show sidebar">
					<BrandLogo src={claraLogo} alt="" aria-hidden="true" />
					<BrandSwap aria-hidden="true">
						<LuPanelRightOpen size={19} />
					</BrandSwap>
					<RailTip>Show sidebar</RailTip>
				</Brand>

				<RailGroup>
					<RailButton type="button" onClick={onNewChat} aria-label="New chat">
						<LuPenSquare size={18} />
						<RailTip>New chat</RailTip>
					</RailButton>
					<RailButton
						type="button"
						onClick={() => {
							setSearching(true)
							onToggle?.()
						}}
						aria-label="Search chats"
					>
						<LuSearch size={18} />
						<RailTip>Search chats</RailTip>
					</RailButton>
					<RailButton type="button" onClick={() => setGuideOpen(true)} aria-label="How Clara works">
						<LuBookOpen size={18} />
						<RailTip>How Clara works</RailTip>
					</RailButton>
				</RailGroup>

				<ClaraGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
			</Rail>
		)
	}

	return (
		<Panel>
			{/* One column, three controls, all the same width. They used to sit on two axes,
			    a full width button beside a small square icon with a search box below at a
			    third width, which is what made the top of the rail read as clutter. */}
			{/* Flat and text forward: icons and labels on a quiet ground, with hover as a
			    soft filled rectangle, rather than a stack of outlined controls. Chrome
			    should be the quietest thing in a rail whose job is to list chats. */}
			<Head>
				{/* The name lives here now, not in the page's top left corner. The rail is
				    where you are when you are thinking about Clara rather than about the
				    patient, and the corner is better spent on the conversation. */}
				<Brandline>
					<Wordmark>
						<WordmarkLogo src={claraLogo} alt="" aria-hidden="true" />
						Clara
						<Version>v2.0 · beta</Version>
					</Wordmark>
					<IconRow>
						<IconButton type="button" onClick={onToggle} aria-label="Hide sidebar">
							<LuPanelRightClose size={19} />
							<Tip>Hide sidebar</Tip>
						</IconButton>
						<IconButton
							type="button"
							onClick={() => setSearching(open => !open)}
							aria-label="Search chats"
							aria-expanded={searching}
						>
							<LuSearch size={18} />
							<Tip>Search chats</Tip>
						</IconButton>
					</IconRow>
				</Brandline>

				{/* The two things you DO, on their own line: start one, or learn how she
				    works. The line above is who she is and how to get out of the way. */}
				<ActionLine>
					<NewChat type="button" onClick={onNewChat}>
						<LuPenSquare size={18} />
						<span>New chat</span>
					</NewChat>
					<GuideButton type="button" onClick={() => setGuideOpen(true)}>
						<LuBookOpen size={18} />
						<span>Guide</span>
					</GuideButton>
				</ActionLine>

				{searching && (
					<SearchBox>
						<LuSearch size={13} />
						<SearchInput
							autoFocus
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder="Search chats"
							aria-label="Search chats"
						/>
					</SearchBox>
				)}
			</Head>

			<Scroll>
				<SectionLabel>Chats</SectionLabel>
				<List ref={listRef} onMouseLeave={() => setHovered(null)}>
					<Highlight
						aria-hidden
						style={{
							top: box?.top ?? 0,
							height: box?.height ?? 0,
							opacity: box ? 1 : 0,
						}}
					/>
					{filtered.length === 0 && <Empty>{query ? "No matching chats." : "No chats yet."}</Empty>}
					{filtered.map(conversation => {
						const id = conversation.conversation_id
						const isActive = id === activeId
						return (
							<RowWrap
								key={id}
								onMouseEnter={() => setHovered(id)}
								onMouseLeave={() => setHovered(null)}
							>
							<Row
								ref={el => {
									itemRefs.current[id] = el
								}}
								type="button"
								onFocus={() => setHovered(id)}
								onClick={() => onOpen?.(id)}
								aria-current={isActive ? "page" : undefined}
							>
								<RowLines>
									<RowText $active={isActive}>
										{conversation.patient_label || conversation.title || conversation.question || "Chat"}
									</RowText>
									{/* The patient names the chat; the question distinguishes two chats about
									    the same patient. With no patient (a documents only chat) the question
									    is the name, and there is no second line to repeat it on. */}
									{conversation.patient_label && (
										<RowSub>{conversation.title || conversation.question || "No questions yet"}</RowSub>
									)}
								</RowLines>
								<RowMeta>{timeAgo(conversation.created_at || conversation.last_at)}</RowMeta>
							</Row>
							</RowWrap>
						)
					})}
				</List>

				{sources.length > 0 && (
					<Sources>
						<SectionLabel>
							<MdMenuBook size={12} /> Sources for this answer
						</SectionLabel>
						{sources.map((source, index) => {
							const id = source.chunk_id ?? index
							const open = openSource === id
							return (
								<SourceCard key={id} $delay={index * 70}>
									<SourceHead
										type="button"
										$open={open}
										aria-expanded={open}
										onClick={() => setOpenSource(open ? null : id)}
									>
										<SourceTitle title={source.document_title}>{source.document_title}</SourceTitle>
										<SourceWhere>{locationOf(source)}</SourceWhere>
									</SourceHead>
									{open && (
										<SourceBody>
											<Quote>{source.text}</Quote>
											{source.source_url && (
												<OpenLink href={source.source_url} target="_blank" rel="noreferrer">
													Open the policy <MdOpenInNew size={11} />
												</OpenLink>
											)}
										</SourceBody>
									)}
								</SourceCard>
							)
						})}
					</Sources>
				)}
			</Scroll>

			<ClaraGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
		</Panel>
	)
}

const Panel = styled.aside`
	/* The page stacks several absolutely positioned layers over the full width: the
	   welcome overlay (z 1), a header bar pinned top/right (z 4), and the old chat dock
	   (z 6). All of them reached across the rail and covered its header, which is why it
	   looked like it began at the search box. Sit above the lot. */
	position: relative;
	z-index: 7;
	height: 100%;
	display: flex;
	flex-direction: column;
	width: 254px;
	flex-shrink: 0;
	padding: 8px;
	gap: 6px;
	border-left: 1px solid var(--color-border-light);
	background: var(--color-surface-panel);
	min-height: 0;
	animation: ${fadeIn} 200ms ease-out both;
`

/* Collapsed: a square button that grows a label on hover. Native title tooltips take
   about a second to appear and render outside the page's own styling, which is too slow
   and too foreign for a two button rail. */
const RailTip = styled.span`
	position: absolute;
	right: calc(100% + 8px);
	top: 50%;
	transform: translateY(-50%) translateX(4px);
	white-space: nowrap;
	padding: 5px 9px;
	border-radius: 7px;
	background: var(--text-color, #1b2330);
	color: var(--color-surface-panel, #fff);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11.5px;
	line-height: 1;
	opacity: 0;
	pointer-events: none;
	transition: opacity 120ms ease, transform 120ms ease;
	box-shadow: 0 4px 14px rgba(16, 24, 40, 0.22);
`

/* The brand mark doubles as the control that opens the rail: on hover the logo fades out
   and the panel icon fades in, so what you are about to do replaces what you were looking
   at. Copied from a sidebar that does exactly this, and it works because the two glyphs
   occupy the same square. */
const BrandLogo = styled.img`
	position: absolute;
	width: 22px;
	height: 22px;
	object-fit: contain;
	transition: opacity 130ms ease;
`

const BrandSwap = styled.span`
	position: absolute;
	display: inline-flex;
	opacity: 0;
	color: var(--text-color);
	transition: opacity 130ms ease;
`

const Brand = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	flex-shrink: 0;
	border: none;
	border-radius: 9px;
	background: transparent;
	cursor: pointer;
	transition: background-color 120ms ease;

	&:hover,
	&:focus-visible {
		background: var(--color-hover-neutral);
	}

	&:hover ${BrandLogo},
	&:focus-visible ${BrandLogo} {
		opacity: 0;
	}

	&:hover ${BrandSwap},
	&:focus-visible ${BrandSwap} {
		opacity: 1;
	}

	&:hover ${RailTip},
	&:focus-visible ${RailTip} {
		opacity: 1;
		transform: translateY(-50%) translateX(0);
	}

	&:focus-visible {
		outline: none;
	}
`

/* The actions, set apart from the mark. The gap is the whole reason the strip reads as
   an identity followed by things to do, rather than as four undifferentiated glyphs. */
const RailGroup = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	margin-top: 18px;
`

const Rail = styled.aside`
	/* The page stacks several absolutely positioned layers over the full width: the
	   welcome overlay (z 1), a header bar pinned top/right (z 4), and the old chat dock
	   (z 6). All of them reached across the rail and covered its header, which is why it
	   looked like it began at the search box. Sit above the lot. */
	position: relative;
	z-index: 7;
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	width: 46px;
	flex-shrink: 0;
	padding: 8px 0;
	border-left: 1px solid var(--color-border-light);
	background: var(--color-surface-panel);
`


const RailButton = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	flex-shrink: 0;
	border: none;
	border-radius: 8px;
	background: transparent;
	color: var(--text-secondary-color);
	cursor: pointer;
	transition: background-color 120ms ease, color 120ms ease;

	&:hover {
		background: var(--color-hover-neutral);
		color: var(--text-color);
	}

	&:hover ${RailTip},
	&:focus-visible ${RailTip} {
		opacity: 1;
		transform: translateY(-50%) translateX(0);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 2px;
	}
`


const Head = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 2px 2px 6px;
	/* Opaque: the rail sits over a page that paints a soft gradient behind everything. */
	background: var(--color-surface-panel);
`

const IconRow = styled.div`
	display: flex;
	align-items: center;
	gap: 2px;
	flex-shrink: 0;
`

/* Both labelled. The guide was an icon with a hover label, and the label opened past the
   rail's right edge and was clipped by the window, which is the one place a tooltip
   cannot go. A word costs less room than a tooltip that does not fit. */
const ActionLine = styled.div`
	display: flex;
	align-items: center;
	gap: 2px;
`


/* Hover labels here too. The icons carried native title tooltips, which take about a
   second to appear and render outside the page's own styling, so three unlabelled glyphs
   sat at the top of the rail saying nothing until you waited. Below the icon rather than
   beside it, because this row starts at the rail's left edge. */
const Tip = styled.span`
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	white-space: nowrap;
	padding: 5px 9px;
	border-radius: 7px;
	background: var(--text-color, #1b2330);
	color: var(--color-surface-panel, #fff);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11.5px;
	line-height: 1;
	opacity: 0;
	transform: translateY(-3px);
	pointer-events: none;
	transition: opacity 120ms ease, transform 120ms ease;
	box-shadow: 0 4px 14px rgba(16, 24, 40, 0.22);
	z-index: 2;
`

const IconButton = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	flex-shrink: 0;
	background: none;
	border: none;
	border-radius: 8px;
	color: var(--text-secondary-color);
	cursor: pointer;
	transition: background-color 120ms ease, color 120ms ease;

	&:hover {
		background: var(--color-hover-neutral);
		color: var(--text-color);
	}

	&:hover ${Tip},
	&:focus-visible ${Tip} {
		opacity: 1;
		transform: translateY(0);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: -2px;
	}
`


const NewChat = styled.button`
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1;
	min-width: 0;
	padding: 8px 12px 8px 8px;
	background: none;
	border: none;
	border-radius: 9px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13.5px;
	font-weight: 500;
	color: var(--text-color);
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 120ms ease;

	&:hover {
		background: var(--color-hover-neutral);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: -2px;
	}
`

const GuideButton = styled(NewChat)`
	flex: 0 0 auto;
	color: var(--text-secondary-color);

	&:hover {
		color: var(--text-color);
	}
`



/* Quiet by default: a line of text with an icon, filling in only while focused. */
const SearchBox = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	height: 30px;
	margin-top: 2px;
	padding: 0 8px;
	border-radius: 8px;
	border: 1px solid transparent;
	background: var(--color-hover-neutral);
	color: var(--text-secondary-color);

	&:focus-within {
		border-color: var(--color-accent-interactive);
	}
`

const SearchInput = styled.input`
	flex: 1;
	min-width: 0;
	background: transparent;
	border: none;
	outline: none;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--text-color);

	&::placeholder {
		color: var(--text-secondary-color);
	}
`

/* The name, where the page's top left corner used to carry it. */
const Brandline = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 2px 2px 6px;
`

const WordmarkLogo = styled.img`
	width: 23px;
	height: 23px;
	object-fit: contain;
	user-select: none;
`

const Version = styled.span`
	flex-shrink: 0;
	white-space: nowrap;
	font-size: 10px;
	font-weight: 500;
	letter-spacing: 0.03em;
	color: var(--text-secondary-color);
	align-self: center;
	padding-top: 2px;
`

const Wordmark = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	white-space: nowrap;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 17px;
	font-weight: 650;
	letter-spacing: -0.015em;
	color: var(--text-color);
`

const RowWrap = styled.div`
	position: relative;
	display: flex;
	align-items: center;
`





const Scroll = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 4px;
`

const SectionLabel = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	padding: 10px 8px 4px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11.5px;
	font-weight: 500;
	color: var(--text-secondary-color);
	opacity: 0.85;
`


const List = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 1px;
`

const Highlight = styled.span`
	position: absolute;
	left: 0;
	right: 0;
	border-radius: 9px;
	background: var(--color-hover-neutral);
	pointer-events: none;
	transition: top 220ms cubic-bezier(0.23, 1, 0.32, 1), height 220ms cubic-bezier(0.23, 1, 0.32, 1),
		opacity 150ms ease;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const Row = styled.button`
	position: relative;
	z-index: 1;
	display: flex;
	align-items: center;
	width: 100%;
	padding: 8px 10px;
	background: none;
	border: none;
	border-radius: 9px;
	text-align: left;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: -2px;
	}
`



/* Two lines per chat: who it is about, then what was asked. */
const RowLines = styled.span`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 1px;
	text-align: left;
`

const RowSub = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11px;
	color: var(--text-secondary-color);
	opacity: 0.75;
`

const RowText = styled.span`
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: ${({ $active }) => ($active ? 600 : 450)};
	color: var(--text-color);
`

const RowMeta = styled.span`
	flex-shrink: 0;
	padding-left: 8px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 10.5px;
	color: var(--text-secondary-color);
	font-variant-numeric: tabular-nums;
`

const Empty = styled.div`
	padding: 10px 8px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
`

const Sources = styled.div`
	display: flex;
	flex-direction: column;
	gap: 5px;
	margin-top: 6px;
	padding-top: 4px;
	border-top: 1px solid var(--color-border-light);
`

const SourceCard = styled.div`
	border: 1px solid var(--color-border-light);
	border-radius: 8px;
	overflow: hidden;
	animation: ${fadeUp} 320ms cubic-bezier(0.23, 1, 0.32, 1) ${({ $delay }) => $delay}ms both;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

const SourceHead = styled.button`
	display: flex;
	flex-direction: column;
	gap: 2px;
	width: 100%;
	padding: 7px 9px;
	background: none;
	border: none;
	border-bottom: 1px solid ${({ $open }) => ($open ? "var(--color-border-light)" : "transparent")};
	text-align: left;
	cursor: pointer;
	transition: background-color 120ms ease;

	&:hover {
		background: var(--color-hover-neutral);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: -2px;
	}
`

const SourceTitle = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 600;
	color: var(--text-color);
`

const SourceWhere = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 10.5px;
	color: var(--text-secondary-color);
`

const SourceBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 7px;
	padding: 8px 9px 9px;
`

const Quote = styled.p`
	margin: 0;
	padding-left: 8px;
	border-left: 2px solid var(--color-border-default);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11.5px;
	line-height: 1.5;
	color: var(--text-secondary-color);
`

const OpenLink = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	width: fit-content;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11px;
	color: var(--text-secondary-color);
	text-decoration: underline;
`

export default ClaraSidebar
